import type { MouseEvent } from 'react'
import { shouldOfferManualAssetRetry, type NoteAsset } from '../assets/assetTypes'
import { APP_SETTINGS } from '../config/appSettings'
import type { Note } from '../domain/note'
import { Icon } from './Icon'

export type NoteUploadStatus = 'pending' | 'error' | null

export function deriveNoteUploadStatus(
  note: Note | null,
  assets: NoteAsset[],
  noteAttemptCount = 0,
  now = Date.now(),
): NoteUploadStatus {
  const visibleAssets = assets.filter((asset) => asset.state !== 'deleted')
  if (
    noteAttemptCount >= APP_SETTINGS.sync.noteRetryMinFailedAttempts
    || visibleAssets.some((asset) => shouldOfferManualAssetRetry(asset, now))
  ) return 'error'
  if (
    noteAttemptCount >= APP_SETTINGS.sync.noteIndicatorMinFailedAttempts
    || note?.localSyncState === 'failed'
    || visibleAssets.some((asset) => (
      asset.state === 'error'
      && asset.attempts >= APP_SETTINGS.sync.noteIndicatorMinFailedAttempts
    ))
  ) return 'pending'
  return null
}

interface UploadStatusIndicatorProps {
  status: NoteUploadStatus
  prominent?: boolean
  onRetry?: () => void
}

export function UploadStatusIndicator({
  status,
  prominent = false,
  onRetry,
}: UploadStatusIndicatorProps) {
  if (!status) return null
  const className = [
    'upload-status-indicator',
    `upload-status-indicator--${status}`,
    prominent ? 'upload-status-indicator--prominent' : '',
  ].filter(Boolean).join(' ')
  if (status === 'error' && onRetry) {
    const retry = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()
      onRetry()
    }
    return (
      <button
        type="button"
        className={className}
        onClick={retry}
        aria-label="Upload failed. Retry now"
        title="Upload failed — retry"
      >
        <Icon name="upload-error" />
      </button>
    )
  }
  return (
    <span
      className={className}
      role="status"
      aria-label={status === 'error' ? 'Upload failed' : 'Pending sync'}
      title={status === 'error' ? 'Upload failed' : 'Pending sync'}
    >
      <Icon name={status === 'error' ? 'upload-error' : 'cloud-upload'} />
    </span>
  )
}
